import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { api, getErrorMessage } from "@/lib/api";
import { TID } from "@/lib/testIds";
import {
  Sparkles, Loader2, ArrowRight, X, Plus, Search, Send, Globe2, Check, Shapes,
} from "lucide-react";
import { SocialLogo } from "@/components/landing/SocialLogos";
import {
  CATEGORY_ICONS, KW_STAGE_GROUPS, PLATFORM_NAMES,
  MAX_SCAN_PLATFORMS, MAX_SCAN_KEYWORDS,
  SCOPE_ICONS, SCOPE_HELPERS, formatScope,
  usePrefersReducedMotion, useIsMobile,
} from "./shared";
import { QuickReplies } from "./ChatShell";
import { GenRibbon } from "./GenLoaders";

/* ------------------------------------------------------------------ */
/* Step 1 · Category (chat-native chips)                               */
/* ------------------------------------------------------------------ */

/**
 * `localOnly` — collect the answer WITHOUT writing it to the workspace.
 *
 * The default path POSTs `/onboarding/select-category`, which sets the
 * workspace's category. That is right for first-run onboarding and WRONG for
 * adding a second brand: brands in a workspace are independent clients, so
 * choosing "Restaurant" for brand #2 must not retag the workspace (and through
 * it, brand #1's future scans). In `localOnly` the answer is handed to the
 * caller and travels on `POST /brands/generate` as the BRAND's own category —
 * which the backend now accepts and stores per-brand.
 */
export function ChatCategoryStep({ onAccepted, localOnly = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [otherDesc, setOtherDesc] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/onboarding/categories");
        if (alive) setItems(res.data?.items || []);
      } catch (e) {
        if (alive) setError("Could not load categories. Please refresh.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? items.filter((c) => c.label.toLowerCase().includes(q) || c.group.toLowerCase().includes(q))
      : items;
    const map = new Map();
    for (const c of filtered) {
      if (!map.has(c.group)) map.set(c.group, []);
      map.get(c.group).push(c);
    }
    return Array.from(map.entries());
  }, [items, query]);

  const submit = async () => {
    if (!selected) return;
    const label = selected === "other" ? (otherDesc || "Other") : (items.find((c) => c.id === selected)?.label || "");
    if (localOnly) {
      // Nothing to persist and nothing that can fail — hand it straight back.
      onAccepted?.(label, selected);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/onboarding/select-category", {
        category_id: selected,
        description: selected === "other" ? otherDesc : undefined,
      });
      onAccepted?.(label, selected);
    } catch (e) {
      setError(getErrorMessage(e, "Something went wrong. Try again."));
      setSubmitting(false);
    }
  };

  if (loading) {
    return <QuickReplies><Loader2 className="w-5 h-5 animate-spin text-lm-primary" /></QuickReplies>;
  }

  return (
    <QuickReplies>
      <div data-testid="onboarding-category-picker" className="rounded-2xl border border-lm-border bg-lm-surface p-4">
        <div className="relative mb-4">
          <Search className="w-4 h-4 text-lm-text3 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search categories…"
            data-testid="onboarding-category-search"
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-lm-border rounded-lg text-lm-text placeholder:text-lm-text3 focus:outline-none focus:ring-2 focus:ring-lm-primary/20 focus:border-lm-primary"
          />
        </div>

        <div className="max-h-[46vh] overflow-y-auto pr-1 space-y-4">
          {groups.length === 0 && (
            <div className="text-sm text-lm-text3 italic px-1">No matches. Try a different search.</div>
          )}
          {groups.map(([groupName, cats]) => (
            <div key={groupName}>
              <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-lm-text3 mb-2 px-1">{groupName}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {cats.map((c) => {
                  const active = selected === c.id;
                  const Icon = CATEGORY_ICONS[c.id] || Shapes;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelected(c.id)}
                      data-testid={`onboarding-category-${c.id}`}
                      className={`flex items-center gap-3 text-left px-3 py-2.5 rounded-xl border transition-all ${
                        active
                          ? "border-lm-primary bg-lm-tint/60 ring-1 ring-lm-primary/30"
                          : "border-lm-border bg-white hover:border-lm-primary/50 hover:bg-lm-tint/30"
                      }`}
                    >
                      <span
                        className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                          active ? "bg-lm-primary text-white" : "bg-lm-tint2 text-lm-primary"
                        }`}
                      >
                        <Icon className="w-[18px] h-[18px]" />
                      </span>
                      <span className={`text-sm leading-snug ${active ? "font-semibold text-lm-text" : "text-lm-text"}`}>
                        {c.label}
                      </span>
                      {active && <Check className="w-4 h-4 text-lm-primary ml-auto shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {selected === "other" && (
          <div className="mt-4">
            <input
              data-testid="onboarding-other-description"
              value={otherDesc}
              onChange={(e) => setOtherDesc(e.target.value)}
              placeholder="One line — e.g. B2B analytics for logistics companies"
              className="w-full px-4 py-2.5 bg-white border border-lm-border rounded-lg text-lm-text placeholder:text-lm-text3 focus:outline-none focus:ring-2 focus:ring-lm-primary/20 focus:border-lm-primary"
            />
          </div>
        )}

        {error && (
          <div data-testid="onboarding-category-error" className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={!selected || submitting || (selected === "other" && !otherDesc.trim())}
          data-testid="onboarding-category-continue"
          className="mt-4 inline-flex items-center gap-2 bg-lm-primary text-white rounded-lg px-5 py-2.5 font-semibold hover:bg-lm-primaryHover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking…</> : <>Send <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
    </QuickReplies>
  );
}

/* ------------------------------------------------------------------ */
/* Step 2 · Market scope (chat-native)                                 */
/* ------------------------------------------------------------------ */

/** `localOnly` — same contract as ChatCategoryStep above, for the market. */
export function ChatScopeStep({ onSaved, initialScope, initialCity, categoryLabel, localOnly = false }) {
  const [scope, setScope] = useState(initialScope || "");
  const [city, setCity] = useState(initialCity || "");
  const [options, setOptions] = useState({ scopes: [], cities: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/onboarding/market-scope/options");
        setOptions(r.data);
      } catch {
        setOptions({
          scopes: [
            { id: "india", label: "India" },
            { id: "us", label: "United States" },
            { id: "au", label: "Australia" },
            { id: "uk", label: "United Kingdom" },
            { id: "global", label: "Anywhere in the world" },
            { id: "local_city", label: "My city (walk-in / local buyers)" },
          ],
          cities: ["New York", "Chicago", "Los Angeles"],
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const canSave = scope && (scope !== "local_city" || (city && city.trim().length > 1)) && !saving;

  const save = async () => {
    if (!canSave) return;
    const label = options.scopes.find((s) => s.id === scope)?.label || formatScope(scope);
    const localCity = scope === "local_city" ? city.trim() : "";
    if (localOnly) {
      onSaved?.(label, localCity, scope);
      return;
    }
    setSaving(true);
    try {
      const payload = scope === "local_city" ? { scope, city: city.trim() } : { scope };
      const r = await api.post("/onboarding/select-market-scope", payload);
      onSaved?.(label, r.data?.city || localCity, scope);
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not save market scope."));
      setSaving(false);
    }
  };

  if (loading) {
    return <QuickReplies><Loader2 className="w-5 h-5 animate-spin text-lm-primary" /></QuickReplies>;
  }

  return (
    <QuickReplies>
      <div data-testid="market-scope-picker" className="rounded-2xl border border-lm-border bg-lm-surface p-4">
        <ul className="space-y-2.5" data-testid="market-scope-options">
          {options.scopes.map((s) => {
            const Icon = SCOPE_ICONS[s.id] || Globe2;
            const active = scope === s.id;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  data-testid={`market-scope-option-${s.id}`}
                  onClick={() => setScope(s.id)}
                  className={`w-full text-left rounded-xl border p-3.5 flex items-start gap-3 transition-all ${
                    active ? "border-lm-primary bg-lm-tint/60" : "border-lm-border bg-white hover:border-lm-primary/50"
                  }`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-lm-primary text-white" : "bg-lm-tint2 text-lm-primary"}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <div className="font-display font-semibold text-sm">{s.label}</div>
                    <div className="text-xs text-lm-text2 mt-0.5">{SCOPE_HELPERS[s.id]}</div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>

        {scope === "local_city" && (
          <div className="mt-3 rounded-xl border border-lm-primary/40 bg-lm-tint/40 p-3.5" data-testid="market-scope-city-panel">
            <label className="block text-xs font-medium uppercase tracking-wider text-lm-text3 mb-2">Your city</label>
            <input
              data-testid="market-scope-city-input"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Start typing — New York, Chicago, Los Angeles…"
              className="w-full border border-lm-border rounded-lg px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-lm-primary/20 focus:border-lm-primary text-lm-text placeholder:text-lm-text3"
              autoFocus
            />
          </div>
        )}

        <button
          type="button"
          onClick={save}
          disabled={!canSave}
          data-testid="market-scope-submit"
          className="mt-4 inline-flex items-center justify-center gap-2 bg-lm-primary text-white rounded-lg px-5 py-2.5 font-semibold hover:bg-lm-primaryHover transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
    </QuickReplies>
  );
}

/* ------------------------------------------------------------------ */
/* Step 3 · Website composer                                           */
/* ------------------------------------------------------------------ */

export function ChatUrlComposer({ url, setUrl, onSubmit, dumpMode, setDumpMode, dump, setDump, minDump, maxDump }) {
  const dumpLen = dump.trim().length;
  const dumpTooShort = dumpLen > 0 && dumpLen < minDump;
  return (
    <QuickReplies>
      <form onSubmit={onSubmit} className="rounded-2xl border border-lm-border bg-lm-surface p-2.5">
        {!dumpMode ? (
          <>
            <div className="flex items-center gap-2">
              <input
                data-testid={TID.onboardingUrlInput}
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://yourdomain.com"
                className="flex-1 border border-lm-border rounded-lg px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-lm-primary/20 focus:border-lm-primary text-lm-text placeholder:text-lm-text3"
                autoFocus
              />
              <button
                type="submit"
                data-testid={TID.onboardingSubmit}
                className="inline-flex items-center justify-center gap-1.5 bg-lm-primary text-white rounded-lg px-4 py-2.5 font-semibold hover:bg-lm-primaryHover transition-colors shrink-0"
              >
                <Send className="w-4 h-4" />
                <span className="hidden sm:inline">Send</span>
              </button>
            </div>
            <button
              type="button"
              onClick={() => setDumpMode(true)}
              className="mt-2 ml-1.5 text-xs text-lm-primary hover:underline"
            >
              Don't have a website? Paste your brand content instead →
            </button>
          </>
        ) : (
          <>
            <textarea
              data-testid="onboarding-dump-input"
              value={dump}
              onChange={(e) => setDump(e.target.value.slice(0, maxDump))}
              maxLength={maxDump}
              rows={6}
              autoFocus
              placeholder="Paste anything about your brand — what you sell, who buys it, your vibe, prices, what makes you different…"
              className="w-full border border-lm-border rounded-lg px-3.5 py-2.5 bg-white text-sm resize-y focus:outline-none focus:ring-2 focus:ring-lm-primary/20 focus:border-lm-primary text-lm-text placeholder:text-lm-text3"
            />
            <div className="flex items-center justify-between mt-1.5 px-1">
              <button type="button" onClick={() => setDumpMode(false)} className="text-xs text-lm-text2 hover:text-lm-text">
                ← Use a website instead
              </button>
              <span className={`text-xs font-mono tabular-nums ${dumpTooShort ? "text-lm-secondary" : "text-lm-text3"}`}>
                {dump.length.toLocaleString()} / {maxDump.toLocaleString()}
              </span>
            </div>
            <button
              type="submit"
              disabled={dumpLen < minDump}
              className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-lm-primary text-white rounded-lg px-4 py-2.5 font-semibold hover:bg-lm-primaryHover transition-colors disabled:opacity-60"
            >
              <Sparkles className="w-4 h-4" /> Analyse my brand
            </button>
            {dumpTooShort && (
              <div className="text-xs text-lm-secondary mt-1.5 px-1">Add a bit more — at least {minDump} characters.</div>
            )}
          </>
        )}
      </form>
    </QuickReplies>
  );
}

/* ------------------------------------------------------------------ */
/* Step 6 · First scan (keywords + platforms, chat-native)             */
/* ------------------------------------------------------------------ */

export function FirstScanStep({ brandId, running, onRun }) {
  const [keywords, setKeywords] = useState([]);
  const [newKw, setNewKw] = useState("");
  const [adding, setAdding] = useState(false);
  const [platformOpts, setPlatformOpts] = useState([]);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (!brandId) return;
    api.get(`/brands/${brandId}`).then((r) => setKeywords(r.data?.keywords || [])).catch(() => {});
    api.get("/scans/platform-options", { params: { brand_id: brandId, feature: "find_customers" } })
      .then((r) => {
        const opts = r.data?.options || [];
        setPlatformOpts(opts);
        setSelected(new Set(opts.filter((o) => o.suggested).map((o) => o.id).slice(0, MAX_SCAN_PLATFORMS)));
      })
      .catch(() => {});
  }, [brandId]);

  const tickedNow = keywords.filter((k) => k.default_selected !== false).length;
  const toggleKw = async (kw) => {
    const next = !(kw.default_selected !== false);
    if (next && tickedNow >= MAX_SCAN_KEYWORDS) {
      toast.info(`Up to ${MAX_SCAN_KEYWORDS} keywords per scan — untick one first.`);
      return;
    }
    setKeywords((ks) => ks.map((k) => (k.keyword_id === kw.keyword_id ? { ...k, default_selected: next } : k)));
    try { await api.patch(`/brands/${brandId}/keywords/${kw.keyword_id}`, { default_selected: next }); } catch (_) {}
  };
  const addKw = async () => {
    const t = newKw.trim();
    if (t.length < 2) return;
    setAdding(true);
    try {
      // If we're already at the cap, add it un-ticked so it doesn't exceed 25 searched.
      const tick = tickedNow < MAX_SCAN_KEYWORDS;
      const r = await api.post(`/brands/${brandId}/keywords`, { text: t, default_selected: tick, stage: "ready" });
      const kw = r.data?.keyword;
      if (kw) setKeywords((ks) => (ks.some((k) => k.keyword_id === kw.keyword_id) ? ks : [...ks, kw]));
      setNewKw("");
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not add keyword."));
    } finally { setAdding(false); }
  };
  const togglePlatform = (id) => setSelected((prev) => {
    const cur = new Set(prev);
    if (cur.has(id)) cur.delete(id);
    else if (cur.size < MAX_SCAN_PLATFORMS) cur.add(id);
    return cur;
  });

  const canRun = tickedNow > 0 && selected.size > 0 && !running;

  return (
    <QuickReplies>
      <div className="rounded-2xl border border-lm-border bg-lm-surface p-4 space-y-4">
        {/* Keywords */}
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-lm-text3 mb-2">
            Keywords to search ({tickedNow}/{MAX_SCAN_KEYWORDS})
          </div>
          <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
            {keywords.map((k) => {
              const on = k.default_selected !== false;
              return (
                <button key={k.keyword_id} type="button" onClick={() => toggleKw(k)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs transition-colors ${
                    on ? "bg-lm-tint2 border-lm-primary text-lm-primary" : "bg-white border-lm-border text-lm-text3 line-through"
                  }`}>
                  {on && <Check className="w-3 h-3 shrink-0" />} {k.text}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2 mt-2.5">
            <input value={newKw} onChange={(e) => setNewKw(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKw(); } }}
              placeholder="Add your own keyword…"
              className="flex-1 border border-lm-border rounded-lg px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-lm-primary/20 focus:border-lm-primary" />
            <button type="button" onClick={addKw} disabled={adding || newKw.trim().length < 2}
              className="inline-flex items-center gap-1 border border-lm-border rounded-lg px-3 py-2 text-sm hover:bg-lm-tint disabled:opacity-50">
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
            </button>
          </div>
        </div>

        {/* Platforms */}
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-lm-text3 mb-2">
            Platforms ({selected.size}/{MAX_SCAN_PLATFORMS})
          </div>
          {platformOpts.length === 0 ? (
            <div className="text-sm text-lm-text3 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {platformOpts.map((o) => {
                const on = selected.has(o.id);
                const atCap = !on && selected.size >= MAX_SCAN_PLATFORMS;
                return (
                  <button key={o.id} type="button" disabled={atCap} onClick={() => togglePlatform(o.id)}
                    className={`inline-flex items-center gap-2 px-2.5 py-2 rounded-lg border text-xs transition-colors ${
                      on ? "bg-lm-tint2 border-lm-primary text-lm-text"
                        : atCap ? "bg-lm-bg border-lm-border text-lm-text3/50 cursor-not-allowed"
                        : "bg-white border-lm-border text-lm-text3 hover:border-lm-primary/40"
                    }`}>
                    <SocialLogo platform={o.id} className="w-4 h-4 shrink-0" />
                    <span className="truncate">{PLATFORM_NAMES[o.id] || o.id}</span>
                    {on && <Check className="w-3.5 h-3.5 ml-auto text-lm-primary shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Run */}
        <button type="button" onClick={() => onRun(Array.from(selected))} disabled={!canRun}
          data-testid="onboarding-run-first-scan"
          className="w-full inline-flex items-center justify-center gap-2 bg-lm-secondary text-white rounded-lg px-5 py-3 text-sm font-semibold hover:bg-lm-secondaryHover disabled:opacity-60">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {running ? "Starting your scan…" : "Run my first scan"}
        </button>
        {tickedNow === 0 && <div className="text-xs text-lm-secondary text-center">Tick at least one keyword to run.</div>}
      </div>
    </QuickReplies>
  );
}

/* ------------------------------------------------------------------ */
/* Step 5 · Review (chat-native card)                                  */
/* ------------------------------------------------------------------ */

export function ChatReview({ brand, setBrand, draft, setDraft, saving, onConfirm, gen, genDone, liveMsg, onRetry, notified, onNotify }) {
  const reduced = usePrefersReducedMotion();
  const mobile = useIsMobile();
  const streaming = !!gen && !genDone;
  const icpsReady = Array.isArray(draft.icps) && draft.icps.length > 0;
  const keywordsReady = Array.isArray(draft.keywords) && draft.keywords.length > 0;
  // The site was thin if the profile came back with little to work from.
  const thinSite = !!draft.profile &&
    (!(draft.profile.value_props || []).length || !(draft.profile.differentiators || []).length);
  const updateProfile = (patch) => setDraft({ ...draft, profile: { ...(draft.profile || {}), ...patch } });
  const updateIcp = (idx, patch) => {
    const next = [...(draft.icps || [])];
    next[idx] = { ...next[idx], ...patch };
    setDraft({ ...draft, icps: next });
  };
  const removeIcp = (idx) => {
    const next = [...(draft.icps || [])];
    next.splice(idx, 1);
    setDraft({ ...draft, icps: next });
  };
  const updateKw = (idx, patch) => {
    const next = [...(draft.keywords || [])];
    next[idx] = { ...next[idx], ...patch };
    setDraft({ ...draft, keywords: next });
  };
  const removeKw = (idx) => {
    const next = [...(draft.keywords || [])];
    next.splice(idx, 1);
    setDraft({ ...draft, keywords: next });
  };
  const selectedCount = (draft.keywords || []).filter((k) => k.default_selected !== false).length;

  // Fair-use cap: at most 15 keywords active — exactly what a scan searches.
  const MAX_ACTIVE = 15;
  const [newKw, setNewKw] = useState("");
  const addKw = () => {
    const t = newKw.trim();
    if (t.length < 2) return;
    setDraft({ ...draft, keywords: [...(draft.keywords || []), { text: t, stage: "ready", default_selected: selectedCount < MAX_ACTIVE }] });
    setNewKw("");
    toast.success(selectedCount < MAX_ACTIVE ? "Added to “Ready to buy”." : `Added — untick one to search it (${MAX_ACTIVE} active max).`);
  };
  const toggleKw = (i, checked) => {
    if (checked && selectedCount >= MAX_ACTIVE) {
      toast.error(`${MAX_ACTIVE} keywords is the max a scan searches. Untick one first.`);
      return;
    }
    updateKw(i, { default_selected: checked });
  };

  // Group keywords by intent stage while preserving each item's original index
  // (all handlers below operate on the flat draft.keywords array by index).
  const kwGroups = KW_STAGE_GROUPS.map((g) => ({
    ...g,
    items: (draft.keywords || [])
      .map((k, i) => ({ k, i }))
      .filter(({ k }) =>
        g.key === "other"
          ? !KW_STAGE_GROUPS.some((gg) => gg.key !== "other" && gg.stages.includes(k.stage))
          : g.stages.includes(k.stage)
      ),
  })).filter((g) => g.items.length > 0);

  return (
    <QuickReplies>
      <div className="rounded-2xl border border-lm-border bg-lm-surface p-4 md:p-5">
        {/* Polite announcements as each section streams in — never steals focus */}
        <p className="sr-only" role="status" aria-live="polite">{liveMsg}</p>

        {/* Slim sticky status ribbon while the rest of the brand still streams */}
        {streaming && <GenRibbon gen={gen} onRetry={onRetry} notified={notified} onNotify={onNotify} />}

        {/* Brand */}
        <Section title="Your brand">
          {thinSite && (
            <div className="mb-3 text-xs text-lm-text2 bg-lm-tint/60 border border-lm-border rounded-lg px-3 py-2.5">
              Your site's light on words, so I've filled in the gaps with my best read — edit anything that feels off.
            </div>
          )}
          <Field label="Brand name">
            <input data-testid={TID.onboardingBrandName} value={brand.name} onChange={(e) => setBrand({ ...brand, name: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Category">
            <input value={draft.profile?.category || ""} onChange={(e) => updateProfile({ category: e.target.value })} className={inputCls} />
          </Field>
          <Field label="1-line summary">
            <textarea rows={2} value={draft.profile?.brand_summary || ""} onChange={(e) => updateProfile({ brand_summary: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Target geography">
            <input value={draft.profile?.target_geography || ""} onChange={(e) => updateProfile({ target_geography: e.target.value })} className={inputCls} />
          </Field>
        </Section>

        {/* ICPs — revealed at stage 1 (~40%). Until then, a reserved-height
            skeleton so nothing below shifts when the cards arrive. */}
        <Section title="Ideal customers" subtitle="Who are you selling to?">
          {!icpsReady ? (
            <RevealSkeleton
              lines={mobile ? 1 : 2}
              label="Getting to know your customers…"
              reduced={reduced}
            />
          ) : (draft.icps || []).map((icp, i) => (
            <div
              key={i}
              style={reduced ? undefined : { animationDelay: `${i * 60}ms` }}
              className={"rounded-xl border border-lm-border bg-white p-4 mb-3 relative " + (reduced ? "" : "animate-[fadeIn_0.3s_ease_both]")}
            >
              <button type="button" aria-label="Remove" onClick={() => removeIcp(i)} className="absolute top-3 right-3 text-lm-text3 hover:text-red-600">
                <X className="w-4 h-4" />
              </button>
              <Field label="Name">
                <input value={icp.name || ""} onChange={(e) => updateIcp(i, { name: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Description">
                <textarea rows={2} value={icp.description || ""} onChange={(e) => updateIcp(i, { description: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Pain points (comma-separated)">
                <input value={(icp.pain_points || []).join(", ")} onChange={(e) => updateIcp(i, { pain_points: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className={inputCls} />
              </Field>
              <Field label="Buying triggers (comma-separated)">
                <input value={(icp.buying_triggers || []).join(", ")} onChange={(e) => updateIcp(i, { buying_triggers: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className={inputCls} />
              </Field>
            </div>
          ))}
        </Section>

        {/* Keywords — revealed at stage 4 (~92%). Skeleton until then. */}
        <Section
          title="Buyer keywords"
          subtitle={keywordsReady
            ? `${selectedCount} of ${MAX_ACTIVE} active (searched) — untick any, edit, or add your own. ${(draft.keywords || []).length} saved total.`
            : "The exact words people type before they buy — coming in last."}
        >
          {!keywordsReady ? (
            <RevealSkeleton lines={mobile ? 3 : 5} label="Writing the exact words people type…" reduced={reduced} />
          ) : (
          <>
          {/* Scrollable panel — keeps ~100 keywords from blowing out the page.
              Grouped by intent stage, chips flow across 1/2/3 responsive columns. */}
          <div className="relative rounded-xl border border-lm-border bg-white/60 max-h-[420px] overflow-y-auto p-3 space-y-4">
            {kwGroups.map((g) => (
              <div key={g.key}>
                <div className="sticky -top-3 z-10 -mx-3 px-3 py-1.5 bg-white/95 backdrop-blur-sm flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-lm-text2">{g.label}</span>
                  <span className="text-[11px] font-medium text-lm-text3 bg-lm-tint rounded-full px-2 py-0.5">{g.items.length}</span>
                </div>
                <div className="mt-2 grid grid-cols-1 gap-1.5">
                  {g.items.map(({ k, i }) => {
                    const on = k.default_selected !== false;
                    return (
                      <div
                        key={i}
                        className={
                          "flex items-center gap-2 rounded-lg border pl-3 pr-2 min-h-[40px] transition-colors " +
                          (on ? "bg-lm-tint2 border-lm-primary" : "bg-white border-lm-border")
                        }
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={(e) => toggleKw(i, e.target.checked)}
                          className="w-4 h-4 accent-lm-primary cursor-pointer shrink-0"
                          aria-label={k.text ? `Search for "${k.text}"` : "Select keyword"}
                        />
                        <input
                          value={k.text || ""}
                          onChange={(e) => updateKw(i, { text: e.target.value })}
                          className={
                            "flex-1 min-w-0 bg-transparent text-sm py-2 focus:outline-none placeholder:text-lm-text3 " +
                            (on ? "text-lm-primary" : "text-lm-text3")
                          }
                          placeholder="a phrase people actually search"
                          aria-label="Edit keyword"
                        />
                        <button
                          type="button"
                          onClick={() => removeKw(i)}
                          className="shrink-0 p-1.5 rounded-full text-lm-text3 hover:text-red-600 hover:bg-red-50"
                          aria-label={k.text ? `Remove "${k.text}"` : "Remove keyword"}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2.5 flex items-center gap-2">
            <input
              value={newKw}
              onChange={(e) => setNewKw(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKw(); } }}
              placeholder="Add your own keyword…"
              data-testid="review-add-keyword-input"
              className="flex-1 border border-lm-border rounded-lg px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-lm-primary/20 focus:border-lm-primary"
            />
            <button
              type="button"
              onClick={addKw}
              disabled={newKw.trim().length < 2}
              data-testid="review-add-keyword"
              className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium text-lm-primary border border-lm-border rounded-lg px-3 py-2 hover:bg-lm-tint disabled:opacity-50"
            >
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
          </>
          )}
        </Section>

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
          <div className="text-sm text-lm-text2">
            {genDone
              ? "Happy with this? You can edit later."
              : (mobile ? "Finishing your buyer words…" : "Ready in a moment — just finishing your buyer words.")}
          </div>
          <button
            data-testid={TID.onboardingConfirm}
            onClick={onConfirm}
            disabled={saving || !genDone}
            aria-disabled={saving || !genDone}
            aria-label={!genDone ? "Confirm — available once your buyer words are ready" : undefined}
            className="inline-flex items-center justify-center gap-2 bg-lm-primary text-white rounded-lg px-6 py-3 min-h-[44px] font-semibold hover:bg-lm-primaryHover disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className={"w-4 h-4" + (reduced ? "" : " animate-spin")} />
              : !genDone ? <Loader2 className={"w-4 h-4" + (reduced ? "" : " animate-spin")} />
              : <Check className="w-4 h-4" />}
            {saving ? "Saving…" : !genDone ? "Finishing up…" : "Confirm & continue"}
          </button>
        </div>
      </div>
    </QuickReplies>
  );
}

/* Reserved-height skeleton for a section that hasn't streamed in yet. Keeps the
   layout stable (no shift when the real cards land) and pulses warmly. Under
   reduced motion it's a calm static block. */
function RevealSkeleton({ lines = 2, label, reduced }) {
  return (
    <div className="rounded-xl border border-dashed border-lm-border bg-lm-tint/20 p-4" data-testid="reveal-skeleton">
      <div className="flex items-center gap-2 mb-3">
        {!reduced && <Loader2 className="w-3.5 h-3.5 animate-spin text-lm-primary" />}
        <span className="text-xs font-medium text-lm-text3">{label}</span>
      </div>
      <div className="space-y-2">
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className={"h-4 rounded-md bg-lm-tint " + (reduced ? "" : "animate-pulse")}
            style={{ width: `${90 - i * 12}%` }}
          />
        ))}
      </div>
    </div>
  );
}

const inputCls =
  "w-full border border-lm-border rounded-lg px-3.5 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-lm-primary/20 focus:border-lm-primary text-sm text-lm-text placeholder:text-lm-text3";

function Section({ title, subtitle, children }) {
  return (
    <section className="mb-6 last:mb-0">
      <div className="mb-3">
        <h2 className="text-base font-display font-semibold">{title}</h2>
        {subtitle && <p className="text-xs text-lm-text2 mt-0.5">{subtitle}</p>}
      </div>
      <div>{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <label className="block text-xs font-medium uppercase tracking-wider text-lm-text3 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
